<template>
  <LibraryLayout>
    <BooksBlock
      v-if="currentlyReadingViewRef && currentlyReadingViewRef.dataLength"
      :view="currentlyReadingViewRef"
      :showFreeBooks="false"
    />
    <NoCurrentlyReadingDocsBlock v-else title="Reading now">
      <p>
        Books you're currently reading will appear here. Find new things to read
        on the Discover page!
      </p>
    </NoCurrentlyReadingDocsBlock>
    <BooksBlock
      v-if="favoritesViewRef && favoritesViewRef.dataLength"
      :view="favoritesViewRef"
      :showFreeBooks="false"
    />

    <EmptyListingBlock v-else title="Favorites" class="empty-favorite">
      <div>
        <p>Books you've marked as a Favorite will appear here.</p>
        <p>
          To mark a book as a Favorite, click the bookmark icon on the book card
          or while reading the book.
        </p>
      </div>
      <img src="@/assets.app/images/favorite-demo.gif" />
    </EmptyListingBlock>
    <BooksBlock
      v-if="finishedViewRef && finishedViewRef.dataLength"
      :view="finishedViewRef"
      :showFreeBooks="false"
    />
    <NoFinishedBooksBlock v-else>
      <p>Books you've finished will appear here.</p>
      <p>
        To mark a book as Finished, click the three dot menu on the book card,
        then click "Move to Finished".
      </p>
    </NoFinishedBooksBlock>
    <BooksBlock
      v-if="offlineBooksViewRef && offlineBooksViewRef.dataLength"
      :view="offlineBooksViewRef"
      data-test-id="offline-block"
      :showFreeBooks="false"
    />
  </LibraryLayout>
</template>

<script setup lang="ts">
import { onBeforeMount, Raw, ref } from 'vue'

import BooksBlock from '@/app/components/listing/BooksBlock.vue'
import NoCurrentlyReadingDocsBlock from '@/app/components/listing/NoCurrentlyReadingDocsBlock.vue'
import NoFinishedBooksBlock from '@/app/components/listing/NoFinishedDocsBlock.vue'
import { BooksView } from '@/models/doc.book'
import { filteredDocsByDocType } from '@/models/doc'

import { useWait } from '@/app/composables/useWait'
import { DocSortOption } from '@/models/doc'
import { Doc } from '@/models/interfaces'
import LibraryLayout from '@/app/components/library/LibraryLayout.vue'
import EmptyListingBlock from '@/app/components/listing/EmptyListingBlock.vue'
import { ListView } from '@/models/docs.list'

const props = defineProps<{
  docs: Doc[]
}>()

const wait = useWait()

const currentlyReadingViewRef = ref<Raw<ListView> | null>(null)
const finishedViewRef = ref<Raw<ListView> | null>(null)
const favoritesViewRef = ref<Raw<ListView> | null>(null)
const offlineBooksViewRef = ref<Raw<ListView> | null>(null)

onBeforeMount(async () => {
  await getBooks()
})

const getBooks = async (): Promise<void> => {
  wait.start('Loading Library')
  try {
    /* TODO: We need to implement offline support for home/docs, #12899.
    
    if (offline.isOffline) {
       await loadBookListFromNativeStorage()
     } */

    const bookData = filteredDocsByDocType(props.docs, 'book')

    const showCtaBlock = false
    const showFreeAtTheTop = false

    const readingNow = await BooksView.createView(
      bookData,
      undefined,
      showCtaBlock,
      showFreeAtTheTop,
    )
    readingNow.setList('reading')
    readingNow.enableContinueReading()
    readingNow.enableActionsButton()
    readingNow.sort(DocSortOption.accessed)
    currentlyReadingViewRef.value = new ListView('Reading Now', readingNow, {
      viewAllLink: '/app/books/list/reading',
    })

    const finished = await BooksView.createView(
      bookData,
      DocSortOption.title,
      showCtaBlock,
      showFreeAtTheTop,
    )
    finished.enableActionsButton()
    finished.setList('finished')
    finishedViewRef.value = new ListView('Finished', finished, {
      viewAllLink: '/app/books/list/finished',
    })

    const favorites = await BooksView.createView(
      bookData,
      DocSortOption.title,
      showCtaBlock,
      showFreeAtTheTop,
    )
    favorites.enableActionsButton()
    favorites.setIsFavorite(true)
    favoritesViewRef.value = new ListView('Favorites', favorites, {
      viewAllLink: '/app/books/list/favorites',
    })

    const offlineBooks = await BooksView.createView(
      bookData,
      undefined,
      showCtaBlock,
      showFreeAtTheTop,
    )
    await offlineBooks.filterByOffline()
    offlineBooksViewRef.value = new ListView('Offline Books', offlineBooks, {
      viewAllLink: '/app/books/list/offline',
    })

    wait.end('Loading Library')
  } catch (error) {
    if (Sentry) {
      Sentry.captureException(error)
    }
    wait.end('Loading Library')
    wait.start('Error Loading Library')
  }
}
</script>
